type EventCallback = (...args: unknown[]) => void;

class EventBusClass {
  private listeners = new Map<string, Set<EventCallback>>();

  on(event: string, callback: EventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  once(event: string, callback: EventCallback): () => void {
    const wrapper = (...args: unknown[]) => {
      this.off(event, wrapper);
      callback(...args);
    };
    return this.on(event, wrapper);
  }

  off(event: string, callback: EventCallback): void {
    this.listeners.get(event)?.delete(callback);
  }

  emit(event: string, ...args: unknown[]): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`[EventBus] Error in handler for "${event}":`, error);
      }
    });
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const EventBus = new EventBusClass();

// Event names
export const Events = {
  // Game lifecycle
  GAME_READY: 'GAME_READY',
  GAME_PAUSED: 'GAME_PAUSED',
  GAME_RESUMED: 'GAME_RESUMED',

  // UI events
  OPEN_PANEL: 'OPEN_PANEL',
  CLOSE_PANEL: 'CLOSE_PANEL',
  SHOW_TOAST: 'SHOW_TOAST',

  // Game events
  PLAYER_UPDATED: 'PLAYER_UPDATED',
  GOLD_CHANGED: 'GOLD_CHANGED',
  LEVEL_UP: 'LEVEL_UP',

  // Combat events (DOM -> Phaser)
  COMBAT_SCENE_READY: 'COMBAT_SCENE_READY',
  COMBAT_DEFEND: 'COMBAT_DEFEND',
  COMBAT_ENEMY_DEFEATED: 'COMBAT_ENEMY_DEFEATED',
  COMBAT_ENEMY_SUMMONED: 'COMBAT_ENEMY_SUMMONED',
  COMBAT_ALLY_SUMMONED: 'COMBAT_ALLY_SUMMONED',
  COMBAT_ENEMY_TRANSFORMED: 'COMBAT_ENEMY_TRANSFORMED',

  // Ability animation events (EventPlayback -> CombatScene)
  COMBAT_ABILITY_USED: 'COMBAT_ABILITY_USED',
  COMBAT_HIT: 'COMBAT_HIT',

  // Charge telegraph (EventPlayback -> CombatScene + FloatingCombatText)
  COMBAT_CAST_TARGET_ENTER: 'COMBAT_CAST_TARGET_ENTER',
  COMBAT_CAST_TARGET_EXIT: 'COMBAT_CAST_TARGET_EXIT',

  // Combat HUD events (Phaser -> DOM)
  COMBATANT_POSITIONS_UPDATED: 'COMBATANT_POSITIONS_UPDATED',
  ACTIVE_TURN_CHANGED: 'ACTIVE_TURN_CHANGED',
  COMBAT_TIMELINE_RESORT: 'COMBAT_TIMELINE_RESORT',

  // Targeting (Phaser <-> DOM)
  COMBAT_TARGET_SELECTED: 'COMBAT_TARGET_SELECTED',
  COMBAT_HIGHLIGHT_TARGETS: 'COMBAT_HIGHLIGHT_TARGETS',
  COMBAT_CLEAR_HIGHLIGHTS: 'COMBAT_CLEAR_HIGHLIGHTS',

  // Debug (DOM -> Phaser)
  DEBUG_CHANGE_BACKGROUND: 'DEBUG_CHANGE_BACKGROUND',
  DEBUG_CHANGE_LIGHTING: 'DEBUG_CHANGE_LIGHTING',

  // HUD lifecycle (DOM -> Phaser)
  HUD_INITIALIZED: 'HUD_INITIALIZED',

  // Combat state (server-driven)
  COMBAT_STATE_CHANGED: 'COMBAT_STATE_CHANGED',
  COMBAT_VICTORY: 'COMBAT_VICTORY',
  COMBAT_DEFEAT: 'COMBAT_DEFEAT',
  COMBAT_LOG: 'COMBAT_LOG',

  // HP/damage events (EventPlayback -> HUD)
  HP_UPDATE: 'HP_UPDATE',
  FLOATING_DAMAGE: 'FLOATING_DAMAGE',
  FLOATING_HEAL: 'FLOATING_HEAL',
  FLOATING_INFO: 'FLOATING_INFO',
  FLOATING_DOT: 'FLOATING_DOT',
  FLOATING_TEMPO: 'FLOATING_TEMPO',
  FLOATING_FERVOR: 'FLOATING_FERVOR',
  FLOATING_FOCUS_DRAIN: 'FLOATING_FOCUS_DRAIN',
  FERVOR_UPDATE: 'FERVOR_UPDATE',
  BREAK_UPDATE: 'BREAK_UPDATE',

  // Crawl flow
  CRAWL_START: 'CRAWL_START',
  CRAWL_ADVANCE: 'CRAWL_ADVANCE',
  CRAWL_COMPLETE: 'CRAWL_COMPLETE',
  COMBAT_ROOM_VICTORY: 'COMBAT_ROOM_VICTORY',
  COMBAT_CRAWL_CELEBRATE: 'COMBAT_CRAWL_CELEBRATE',
  CRAWL_CELEBRATE_DONE: 'CRAWL_CELEBRATE_DONE',

  // Status effect HUD updates
  STATUS_EFFECTS_UPDATE: 'STATUS_EFFECTS_UPDATE',

  // Playback lifecycle
  COMBAT_PLAYBACK_COMPLETE: 'COMBAT_PLAYBACK_COMPLETE',

  // Scene management
  COMBAT_SCENE_RESTART: 'COMBAT_SCENE_RESTART',
  COMBAT_ROOM_TRANSITION: 'COMBAT_ROOM_TRANSITION',
  COMBAT_FLEE_SUCCESS: 'COMBAT_FLEE_SUCCESS',

  // Navigation
  NAVIGATE: 'NAVIGATE',
  NAVIGATE_BACK: 'NAVIGATE_BACK',
} as const;
